"use client";

import { createContext, useContext, useEffect, useState } from "react";
import {
  COOKIE_CONSENT_COOKIE,
  type CookieChoices,
  type CookieConsent,
  isCookieAllowed,
  parseCookieConsent,
  serializeCookieConsent,
} from "shared/CookieConsent";
import { getCookie, getDocumentCookieNames, takeUnconsentedCookies } from "src/lib/AppSharedUtils";
import { updateGoogleAnalyticsConsent } from "src/lib/GoogleAnalytics";
import { setCookie, useCookie } from "src/lib/hooks/ClientHooks";

export const getCookieConsent = (): CookieConsent | null =>
  parseCookieConsent(getCookie(COOKIE_CONSENT_COOKIE.name));

/**
 * Records the visitor's choice in the essential consent cookie. Allowing functional cookies
 * persists preferences kept in memory so far; refusing a category deletes its cookies already on
 * the device (HttpOnly ones are removed by the server on the next page response).
 */
export const saveCookieConsent = (consent: CookieChoices) => {
  updateGoogleAnalyticsConsent(consent.analytics === true, consent.marketing === true);
  setCookie(COOKIE_CONSENT_COOKIE.name, serializeCookieConsent(consent), {
    expiresMs: COOKIE_CONSENT_COOKIE.expirationMs,
  });

  if (consent.functional) {
    for (const [name, { value, expiresMs }] of takeUnconsentedCookies()) {
      setCookie(name, value, { expiresMs });
    }
  }

  for (const name of getDocumentCookieNames()) {
    if (!isCookieAllowed(name, getCookieConsent())) {
      setCookie(name, null);
      // GA uses host-only cookies; our preference cookies use the shared site domain.
      // biome-ignore lint/suspicious/noDocumentCookie: Delete host-only analytics cookies as well as shared-domain cookies.
      document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/`;
    }
  }
};

export interface CookieConsentState {
  /** False until the choice is known: from the request on the server, or after mount without it. */
  ready: boolean;
  consent: CookieConsent | null;
  functionalAllowed: boolean;
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
}

/**
 * The cookie choice the request carried, read on the server by the root loader (`undefined` when
 * the loader didn't run). It never changes after hydration: updating a context above content that
 * is still hydrating makes React discard that content's server HTML.
 */
export const RequestCookieConsentContext = createContext<CookieConsent | null | undefined>(
  undefined,
);

/**
 * The visitor's current cookie choice. The server renders with the request's choice (cached pages
 * are keyed by consent state, src/lib/CFCache.ts), hydration uses the same value, and after mount it
 * follows the cookie, so a choice made in the browser applies at once.
 */
export const useCookieConsent = (): CookieConsentState => {
  const requestConsent = useContext(RequestCookieConsentContext);
  const [getConsentCookie] = useCookie(COOKIE_CONSENT_COOKIE.name);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const consent = mounted ? parseCookieConsent(getConsentCookie()) : (requestConsent ?? null);
  return {
    ready: mounted || requestConsent !== undefined,
    consent,
    functionalAllowed: consent?.functional === true,
    analyticsAllowed: consent?.analytics === true,
    marketingAllowed: consent?.marketing === true,
  };
};
